import React, { memo } from 'react';

import { SharedDescriptionListProps } from '../../types/SharedDescriptionListProps';
import { UserAction } from '../../types/UserAction';
import { Actions } from '../Actions/Actions';
import { Box } from '../Box/Box';
import { CollapsibleText } from '../CollapsibleText/CollapsibleText';
import { Grid } from '../Grid/Grid';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { MarkedText } from '../MarkedText/MarkedText';
import { TruncateText } from '../Text/subcomponents/TruncateText';
import { Text } from '../Text/Text';

interface Props extends SharedDescriptionListProps {
  /** Terms */
  terms: {
    title: string;
    actions?: UserAction[];
    tooltip?: string;
    description: React.ReactNode;
  }[];
}

const termTitleVariant = (isCondensed: boolean) =>
  isCondensed ? 'body' : 'tiny';
const termDescriptionVariant = (isCondensed: boolean) =>
  isCondensed ? 'tiny' : 'body-medium';
const termColor = (isCondensed: boolean, type: 'title' | 'description') =>
  isCondensed
    ? type === 'title'
      ? 'text.primary'
      : 'text.secondary'
    : type === 'title'
      ? 'text.secondary'
      : 'text.primary';

export const DescriptionList = memo(
  ({
    columns,
    columnsMinWidth,
    highlighter,
    limit,
    mode,
    search,
    terms,
  }: Props) => {
    const isCardMode = mode === 'card';
    const border = isCardMode ? 'border' : undefined;
    const p = isCardMode ? 4 : undefined;
    const isCondensedMode = mode === 'condensed';
    const isRowMode = mode === 'row';

    const rowModeColumns =
      isRowMode && (columns === undefined || columns === 'auto-fit')
        ? 'auto-fit'
        : (columns ?? 1);

    const renderTitle = (
      title: string,
      tooltip?: string,
      actions?: Props['terms'][number]['actions'],
    ) => (
      <>
        <TruncateText
          shouldTruncate
          color={termColor(isCondensedMode, 'title')}
          variant={termTitleVariant(isCondensedMode)}
        >
          <MarkedText text={title} search={search} />
        </TruncateText>
        <Layout align="center">
          {tooltip && <Icon icon="info" tooltip={tooltip} color="inherit" />}
          {actions && (
            <Layout>
              <Actions actions={actions} size="xs" />
            </Layout>
          )}
        </Layout>
      </>
    );

    const renderDescription = (description: React.ReactNode) =>
      typeof description === 'string' ? (
        <CollapsibleText
          highlighter={highlighter}
          limit={limit}
          search={search}
          text={description}
          variant={termDescriptionVariant(isCondensedMode)}
          color={termColor(isCondensedMode, 'description')}
          whiteSpace="pre-wrap"
        />
      ) : (
        <Text
          variant={termDescriptionVariant(isCondensedMode)}
          color={termColor(isCondensedMode, 'description')}
          whiteSpace="pre-wrap"
        >
          {description}
        </Text>
      );

    return (
      <Grid
        as="dl"
        border={border}
        columns={isRowMode ? rowModeColumns : columns}
        columnSpacing={isRowMode ? 3 : 8}
        columnMinWidth={
          isRowMode && rowModeColumns === 'auto-fit'
            ? (columnsMinWidth ?? 350)
            : columnsMinWidth
        }
        m={0}
        p={p}
        rowSpacing={isCondensedMode ? 3 : 6}
        width={isRowMode && rowModeColumns === 'auto-fit' ? '100%' : undefined}
      >
        {terms.map(({ title, description, tooltip, actions }) => {
          const termContent = (
            <>
              <Layout
                as="dt"
                direction="row"
                align="flex-start"
                {...(isRowMode && { minW: '120px', maxW: '120px' })}
              >
                {renderTitle(title, tooltip, actions)}
              </Layout>
              <Box as="dd" m={0} {...(isRowMode && { minW: 0 })}>
                {renderDescription(description)}
              </Box>
            </>
          );
          return isRowMode ? (
            <Layout
              key={title}
              as="div"
              direction="row"
              role="gridcell"
              spacing={2}
              minW={0}
            >
              {termContent}
            </Layout>
          ) : (
            <div key={title} role="gridcell">
              {termContent}
            </div>
          );
        })}
      </Grid>
    );
  },
);
